/**
 * Utility functions for processing and rendering chat content
 */
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Convert custom table syntax to standard markdown tables
 */
export const convertCustomTableToMarkdown = (content: string): string => {
  return content.replace(
    /\[TABLE\]([\s\S]*?)\[\/TABLE\]/g,
    (match, tableContent) => {
      const lines = tableContent
        .trim()
        .split("\n")
        .filter((line: string) => line.trim());
      if (lines.length === 0) return match;

      const headerLine = lines[0];
      const headers = headerLine.split("|").map((h: string) => h.trim());

      let markdownTable = "\n| " + headers.join(" | ") + " |\n";
      markdownTable += "| " + headers.map(() => "---").join(" | ") + " |\n";

      for (let i = 1; i < lines.length; i++) {
        const cells = lines[i].split("|").map((c: string) => c.trim());
        markdownTable += "| " + cells.join(" | ") + " |\n";
      }

      return markdownTable;
    }
  );
};

/**
 * Get tool icon and animation class based on tool name and content
 */
const getToolIconAndClass = (toolName: string, toolContent: string, isStreaming: boolean) => {
  // Check for ERP API tool
  if (toolName && toolName.includes("call_erp_api")) {
    // Determine specific action based on content
    if (toolContent.includes("Checking requirements")) {
      return { icon: "📋", iconClass: isStreaming ? "checking" : "" };
    } else if (toolContent.includes("Looking up")) {
      return { icon: "🔍", iconClass: isStreaming ? "searching" : "" };
    } else if (toolContent.includes("Preparing to create")) {
      return { icon: "➕", iconClass: isStreaming ? "creating" : "" };
    } else if (toolContent.includes("Preparing to update")) {
      return { icon: "✏️", iconClass: isStreaming ? "updating" : "" };
    } else if (toolContent.includes("Preparing to delete")) {
      return { icon: "🗑️", iconClass: isStreaming ? "deleting" : "" };
    } else {
      // Generic ERP API call
      return { icon: "🔗", iconClass: isStreaming ? "api-call" : "" };
    }
  }

  // Check if it's a search tool by tool name or by content containing "Searching:"
  const isSearchTool =
    (toolName && toolName.includes("search_knowledge_base")) ||
    (toolContent && toolContent.includes("**Searching:**"));

  const icon = isSearchTool ? "🔍" : "📝";
  const iconClass = isStreaming ? (isSearchTool ? "searching" : "writing") : "";

  return { icon, iconClass };
};

/**
 * Render message content with inline tool searches
 */
export const renderMessageWithToolSearches = (content: string, isStreaming: boolean = false) => {
  // Split content by tool search markers with tool name
  const parts = content.split(
    /\[TOOL_SEARCH:(.*?)\]([\s\S]*?)\[\/TOOL_SEARCH\]/g
  );

  return parts.map((part, idx) => {
    // Indices: 0=content, 1=toolName, 2=toolContent, 3=content, 4=toolName, 5=toolContent...
    if (idx % 3 === 0) {
      // Regular content
      return part ? (
        <ReactMarkdown key={idx} remarkPlugins={[remarkGfm]}>
          {convertCustomTableToMarkdown(part)}
        </ReactMarkdown>
      ) : null;
    } else if (idx % 3 === 1) {
      // Tool name - skip rendering (will be used in next iteration)
      return null;
    } else {
      // Tool content (idx % 3 === 2)
      const toolName = parts[idx - 1]; // Get the tool name from previous index
      const toolContent = part;

      const { icon, iconClass } = getToolIconAndClass(toolName, toolContent, isStreaming);

      return (
        <div key={idx} className="tool-search-item">
          <span className={`tool-search-icon ${iconClass}`}>{icon}</span>
          <span className="tool-search-text">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{ p: "span" }}
            >
              {part}
            </ReactMarkdown>
          </span>
        </div>
      );
    }
  });
};

/**
 * Render stream sequence from workflow metadata
 */
export const renderStreamSequence = (sequence: any[], finalContent: string) => {
  // Build the full timeline from stream_sequence
  let intermediateText = "";
  const elements: React.ReactElement[] = [];
  let partIndex = 0;

  sequence.forEach((item, idx) => {
    // Skip thinking chunks - they're shown in the collapsible Reasoning section
    if (item.node === "extended_thinking") {
      return;
    }

    if (item.node === "agent_response" && item.type === "response_chunk") {
      // Accumulate response chunks from agent
      intermediateText += item.content;
    } else if (item.node === "tool_execution" && item.type === "tool_start") {
      // When we hit a tool, save accumulated text and add tool search
      if (intermediateText.trim()) {
        elements.push(
          <ReactMarkdown
            key={`text-${partIndex++}`}
            remarkPlugins={[remarkGfm]}
          >
            {convertCustomTableToMarkdown(intermediateText)}
          </ReactMarkdown>
        );
        intermediateText = "";
      }

      // Determine icon based on tool name and content
      const toolName = item.tool_name || "";
      const toolContent = item.content || "";
      const { icon, iconClass } = getToolIconAndClass(toolName, toolContent, false);

      // Add tool search - simple display
      elements.push(
        <div key={`tool-${partIndex++}`} className="tool-search-item">
          <span className={`tool-search-icon ${iconClass}`}>{icon}</span>
          <span className="tool-search-text">
            {" "}
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{ p: "span" }}
            >
              {item.content}
            </ReactMarkdown>
          </span>
        </div>
      );
    }
  });

  // Add any remaining accumulated text
  if (intermediateText.trim()) {
    elements.push(
      <ReactMarkdown key={`text-${partIndex++}`} remarkPlugins={[remarkGfm]}>
        {convertCustomTableToMarkdown(intermediateText)}
      </ReactMarkdown>
    );
  }

  // Add final content at the end only if not already added
  if (finalContent && typeof finalContent === 'string' && finalContent.trim() && !intermediateText.trim()) {
    elements.push(
      <ReactMarkdown key={`final-${partIndex}`} remarkPlugins={[remarkGfm]}>
        {convertCustomTableToMarkdown(finalContent)}
      </ReactMarkdown>
    );
  }

  return <>{elements}</>;
};
